import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Link, Stack } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../constants';
import { SNUS_PROJECT_KEY } from './snusProductUtils';

const apiBase = `${API_URL}/sweden-snus-admin`;

export function reportShareUrl(hash) {
  return `${window.location.origin}/report/${hash}`;
}

export default function PublishReportControls({ payload, disabled }) {
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const payloadKey = useMemo(() => JSON.stringify(payload || {}), [payload]);

  useEffect(() => {
    setPublished(null);
    setCopied(false);
    setError(null);
  }, [payloadKey]);

  const publish = async () => {
    setPublishing(true);
    setError(null);
    setCopied(false);
    try {
      const res = await axios.post(`${apiBase}/reports`, payload, {
        withCredentials: true,
        headers: { 'content-type': 'application/json' },
      });
      setPublished(res.data);
    } catch (e) {
      setPublished(null);
      setError(e.response?.data?.detail || e.message || 'Failed to publish report page');
    } finally {
      setPublishing(false);
    }
  };

  const shareUrl = published?.hash ? reportShareUrl(published.hash) : '';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
    } catch {
      setCopied(false);
      setError('Could not copy the link. Select it and copy it manually.');
    }
  };

  return (
    <>
      <Button variant="outlined" disabled={disabled || publishing} onClick={publish}>
        {publishing ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
        {publishing ? 'Publishing…' : 'Publish page'}
      </Button>
      {(error || published) && (
        <Box sx={{ flexBasis: '100%', width: '100%' }}>
          {error && (
            <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
              {typeof error === 'string' ? error : JSON.stringify(error)}
            </Alert>
          )}
          {published?.hash && (
            <Alert severity="success" sx={{ mt: 1 }} onClose={() => setPublished(null)}>
              <Stack spacing={0.5}>
                <span>
                  {published.created ? 'Published a new report page.' : 'Refreshed the existing report page.'}{' '}
                  The same selection keeps this link.
                </span>
                <Link href={shareUrl} target="_blank" rel="noopener noreferrer">
                  {shareUrl}
                </Link>
                <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                  <Link component="button" type="button" onClick={copyLink}>
                    {copied ? 'Copied' : 'Copy link'}
                  </Link>
                  <Link href={shareUrl} target="_blank" rel="noopener noreferrer">
                    Open outside
                  </Link>
                  <Link
                    component={RouterLink}
                    to={`/${SNUS_PROJECT_KEY}/shelf-reports?hash=${encodeURIComponent(published.hash)}`}
                  >
                    View embedded
                  </Link>
                </Stack>
              </Stack>
            </Alert>
          )}
        </Box>
      )}
    </>
  );
}
